import { FileText, ExternalLink, Link2, Download, Sheet } from "lucide-react";

export const abs = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
export const sourceImage = (a) => (a.kind === "pdf" ? abs(`${a.url}/page/${a.page || 0}.png`) : abs(a.url));

/** Renders an image cropped with pure CSS (works in the PDF export too). crop = fractions of the source. */
export function CroppedImage({ a, className = "" }) {
  const c = a.crop;
  if (!c || !a.aspect) {
    return <img src={sourceImage(a)} alt={a.caption || a.title || ""} crossOrigin="anonymous" data-pdf-visual className={`w-full h-auto border border-brand-border ${className}`} />;
  }
  const ratio = (c.w * a.aspect) / c.h;
  return (
    <div data-pdf-visual className={`relative w-full overflow-hidden border border-brand-border ${className}`} style={{ aspectRatio: ratio }}>
      <img src={sourceImage(a)} alt={a.caption || a.title || ""} crossOrigin="anonymous"
        style={{ position: "absolute", width: `${100 / c.w}%`, maxWidth: "none", left: `${(-c.x / c.w) * 100}%`, top: `${(-c.y / c.h) * 100}%` }} />
    </div>
  );
}

export const isVisual = (a) => a.kind === "image" || a.kind === "pdf";

function SheetTable({ a }) {
  const t = a.table || { headers: [], rows: [] };
  return (
    <figure data-testid={`report-att-sheet-${a.id}`} data-pdf-hide>
      <div className="overflow-x-auto border border-brand-border bg-brand-surface">
        <table className="w-full text-xs">
          <thead><tr>{t.headers.map((h, i) => <th key={i} className="text-left font-medium text-brand-ink px-3 py-2 border-b border-brand-border whitespace-nowrap bg-brand-hover">{h}</th>)}</tr></thead>
          <tbody>
            {t.rows.map((r, i) => (
              <tr key={i} className="border-b border-brand-border last:border-0">
                {r.map((c, j) => <td key={j} className={`px-3 py-1.5 whitespace-nowrap ${/^[-$(]?[\d,.]+%?\)?$/.test(c) ? "text-right font-mono" : ""} text-brand-muted`}>{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <figcaption className="text-xs text-brand-tertiary mt-2 flex items-center justify-between gap-2">
        <span>{a.caption || t.sheet}{t.total_rows > t.rows.length ? ` · showing ${t.rows.length} of ${t.total_rows} rows` : ""}</span>
        <a href={abs(a.url)} className="inline-flex items-center gap-1 text-brand-primary shrink-0" data-testid={`report-att-sheet-dl-${a.id}`}>Download <Download className="h-3 w-3" /></a>
      </figcaption>
    </figure>
  );
}

function LinkChip({ a, pdfOnly = false }) {
  const Icon = a.kind === "link" ? Link2 : a.kind === "sheet" ? Sheet : FileText;
  return (
    <a href={abs(a.url)} target="_blank" rel="noreferrer" data-testid={pdfOnly ? undefined : `report-att-link-${a.id}`} {...(pdfOnly ? { "data-pdf-only": "" } : {})}
      className={`${pdfOnly ? "hidden" : "inline-flex"} items-center gap-2 rounded-full border border-brand-border bg-brand-surface px-3 py-1.5 text-xs text-brand-ink hover:border-brand-primary transition-colors`}>
      <Icon className="h-3.5 w-3.5 text-brand-gold" /> {a.title || a.url} <ExternalLink className="h-3 w-3 text-brand-tertiary" />
    </a>
  );
}

export function AttachmentsView({ items = [] }) {
  if (!items.length) return null;
  const inline = items.filter((a) => isVisual(a) && a.display !== "link");
  const sheets = items.filter((a) => a.kind === "sheet" && a.display !== "link");
  const links = items.filter((a) => !inline.includes(a) && !sheets.includes(a));
  const collapsed = [...inline.filter((a) => a.kind === "pdf"), ...sheets];
  return (
    <div className="mt-4 space-y-4" data-testid="report-attachments">
      {inline.length > 0 && (
        <div className={`grid gap-4 ${inline.length > 1 ? "sm:grid-cols-2" : ""}`}>
          {inline.map((a) => (
            <figure key={a.id} data-testid={`report-att-inline-${a.id}`} {...(a.kind === "pdf" ? { "data-pdf-hide": "" } : {})}>
              <CroppedImage a={a} />
              {(a.caption || a.kind === "pdf") && (
                <figcaption className="text-xs text-brand-tertiary mt-2 flex items-center justify-between gap-2">
                  <span>{a.caption}</span>
                  {a.kind === "pdf" && <a href={abs(a.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand-primary shrink-0">Open PDF{a.page_count > 1 ? ` (${a.page_count} pages)` : ""} <ExternalLink className="h-3 w-3" /></a>}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      )}
      {sheets.map((a) => <SheetTable key={a.id} a={a} />)}
      {(links.length > 0 || collapsed.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {links.map((a) => <LinkChip key={a.id} a={a} />)}
          {collapsed.map((a) => <LinkChip key={`pdf-${a.id}`} a={{ ...a, title: a.title || a.caption || "Attachment" }} pdfOnly />)}
        </div>
      )}
    </div>
  );
}
